import * as React from "react";
import {
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {makeStyles} from "tss-react/mui";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import type {LearnerAssignedAnnouncementDto} from "@/app2/shared/data/gen";
import {getAnnouncementAuthorDisplayDate} from "@/app2/pages/ManagerView/Pages/Announcements/announcementAdminStatus";
import {AnnouncementAuthorComponent} from "@/pages/HomePage/AnnouncementsSection/AnnouncementCard";
import {IUserEntity} from "@buildwitt/shapeshift-types/entities";
import {useRef} from "react";
import {Files} from "@buildwitt/shapeshift-common/v1/files";
import {Links} from "@buildwitt/shapeshift-common/v1/links";
import {WYSIWYG} from "@buildwitt/shapeshift-common/v1/wysiwyg";
import {useQueryClient} from "@tanstack/react-query";
import {ANNOUNCEMENTS_QUERY_KEYS} from "@/app2/shared/data/repos/announcements";
import {App2AnnouncementPageContent} from "./App2AnnouncementPageContent";
import {useOrientation} from "@/hooks/useOrientation";
import {landscapeVideoContainerStyles} from "@/styles/landscapeVideoPlayerStyles";

const useStyles = makeStyles<{landscapeVideoFullscreen: boolean}>()((theme, {landscapeVideoFullscreen}) => ({
	// Same embedded-modal behavior as legacy AnnouncementPageContent (isForHomePage).
	landscapeVideoContainer: landscapeVideoFullscreen ? landscapeVideoContainerStyles : {},
	dialogPaper: {
		backgroundColor: theme.palette.background.paperElevation2,
		borderColor: theme.palette.background.paperElevation2,
		borderRadius: 16,
		marginTop: 61,
		color: "black"
	},
	dialogContentRoot: {
		padding: "5px",
		textAlign: "left"
	},
	dialogActionsRoot: {
		justifyContent: "flex-start"
	},
	dialogTitle: {
		padding: 0,
		paddingBottom: "10px",
		fontSize: "24px",
		textAlign: "left"
	},
	closeButton: {
		position: "absolute",
		right: 8,
		top: 8
	},
	title: {
		marginTop: "8px",
		marginBottom: "8px",
		fontSize: "34px"
	},
	content: {
		marginTop: "8px",
		marginBottom: "8px",
		fontSize: "16px",
		color: "rgba(249, 249, 249, 0.7)",
		fontWeight: 400
	},
	authorName: {
		fontSize: "14px",
		color: theme.palette.text.secondary
	},
	richContent: {
		marginTop: "8px",
		marginBottom: "16px",
		fontSize: "16px",
		color: "rgba(249, 249, 249, 0.7)",
		"& p": {lineHeight: 1.5},
		"& a": {
			textDecoration: "underline",
			color: theme.palette.primary.main
		}
	},
	attachmentsSectionTitle: {
		color: "#FFFFFF",
		fontFamily: "Titillium Web, sans-serif",
		fontWeight: 600,
		fontSize: "20px",
		lineHeight: "150%",
		letterSpacing: "0.15px",
		marginTop: "16px",
		marginBottom: "8px"
	},
	attachmentsFiles: {
		marginBottom: "16px",
		"& > div": {
			display: "flex",
			flexWrap: "wrap",
			alignItems: "center",
			background: "transparent",
			padding: "12px 0",
			marginBottom: "8px",
			gap: "8px",
			position: "relative",
			paddingLeft: "22px"
		},
		"& > div::before": {
			content: "\"\"",
			position: "absolute",
			left: 0,
			top: "50%",
			transform: "translateY(-50%)",
			width: "18px",
			height: "18px",
			flexShrink: 0,
			backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
				`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="${theme.palette.primary.main}" d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5C7 20.54 9.46 23 12.5 23s5.5-2.46 5.5-5.5V6h-1.5z"/></svg>`
			)}")`,
			backgroundRepeat: "no-repeat",
			backgroundPosition: "center",
			backgroundSize: "contain"
		},
		"& > div > div:first-of-type": {
			display: "none"
		},
		"& > div > div:nth-of-type(2)": {
			margin: "0 !important",
			flexGrow: 1,
			minWidth: 0
		},
		"& > div > div:nth-of-type(2) h6": {
			fontFamily: "Titillium Web, sans-serif",
			fontWeight: 400,
			fontSize: "16px",
			lineHeight: "140%",
			letterSpacing: "0.15px",
			textDecoration: "underline",
			textDecorationStyle: "solid",
			color: theme.palette.primary.main,
			margin: 0
		},
		"& > div > a": {
			position: "absolute",
			left: 0,
			top: 0,
			right: 0,
			bottom: 0,
			zIndex: 1,
			border: "none",
			padding: 0,
			background: "transparent"
		},
		"& > div > a > div": {
			visibility: "hidden"
		}
	},
	attachmentsLinks: {
		marginBottom: "16px",
		"& a": {
			fontFamily: "Titillium Web, sans-serif",
			fontWeight: 400,
			fontSize: "16px",
			lineHeight: "140%",
			letterSpacing: "0.15px",
			textDecoration: "underline",
			textDecorationStyle: "solid",
			textDecorationSkipInk: "auto",
			color: theme.palette.primary.main
		}
	}
}));

interface IProps {
	open: boolean;
	onClose: () => void;
	announcement: LearnerAssignedAnnouncementDto | null;
	onComplete: (slug: string) => void;
}

export const App2AnnouncementModal: React.FC<IProps> = ({
	open,
	onClose,
	announcement,
	onComplete
}) => {
	const {t} = useSSTranslation();
	const mq = useMq();
	const isLandscape = useOrientation();
	const landscapeVideoFullscreen = open && Boolean(announcement?.videoUrl) && isLandscape;
	const {classes, cx} = useStyles({landscapeVideoFullscreen});
	const videoContainerRef = useRef<HTMLDivElement | null>(null);
	const queryClient = useQueryClient();

	const handleClose = (): void => {
		// Unmount video first so Brightcove/Video.js is disposed before cache refetch re-renders.
		onClose();
		if (announcement?.slug != null && announcement.complete !== true) {
			onComplete(announcement.slug);
		}
		// Defer refetch until after App2AnnouncementPageContent unmount flush (WebSocket + cache patch).
		queueMicrotask(() => {
			void queryClient.refetchQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.LEARNER_ASSIGNED]});
		});
	};

	const authorDisplayDate =
		announcement != null
			? getAnnouncementAuthorDisplayDate({
				publishedAt: announcement.publishedAt,
				scheduledDeliveryDate: announcement.scheduledDeliveryDate
			})
			: new Date();

	return (
		<Dialog
			open={open}
			fullScreen={mq.mobile}
			onClose={handleClose}
			aria-label="Announcement dialog"
			aria-modal="true"
			data-test-id="app2-announcement-modal"
			PaperProps={{
				className: classes.dialogPaper,
				style: {
					minHeight: "700px",
					paddingBottom: mq.mobile ? "40px" : "10px"
				}
			}}
		>
			<DialogTitle
				id="app2-announcement-dialog-title"
				className={classes.dialogTitle}
				data-test-id="app2-announcement-modal-title"
			>
				{t("common_announcement", "Announcement")}
			</DialogTitle>

			<IconButton
				onClick={handleClose}
				className={classes.closeButton}
				aria-label="Close announcement dialog"
				data-test-id="app2-announcement-modal-close-btn"
			>
				<CloseIcon />
			</IconButton>

			{announcement && (
				<>
					{open && announcement.videoUrl && (
						<Box
							ref={videoContainerRef}
							className={cx(landscapeVideoFullscreen && classes.landscapeVideoContainer)}
							aria-label="Announcement media content"
							data-test-id="announcement-media-container"
						>
							<App2AnnouncementPageContent announcement={announcement} />
						</Box>
					)}
					<DialogContent
						className={classes.dialogContentRoot}
						aria-label="Announcement content"
						data-test-id="app2-announcement-modal-content"
					>
						<Typography
							variant="h4"
							className={classes.title}
							data-test-id="app2-announcement-title"
						>
							{announcement.title ?? ""}
						</Typography>

						<Typography
							className={classes.content}
							variant="body1"
							data-test-id="app2-announcement-subtitle"
						>
							{announcement.subtitle ?? ""}
						</Typography>

						{announcement?.content && (
							<WYSIWYG
								className={classes.richContent}
								data-test-id="announcement-rich-content"
							>
								{announcement.content}
							</WYSIWYG>
						)}

						{(() => {
							const raw = announcement?.files;
							const filesList = Array.isArray(raw) ? raw : raw ? [raw] : [];
							return (
								filesList.length > 0 && (
									<>
										<Typography
											className={classes.attachmentsSectionTitle}
											variant="subtitle1"
										>
											Files
										</Typography>
										<Files
											className={classes.attachmentsFiles}
											files={filesList}
										/>
									</>
								)
							);
						})()}

						{(() => {
							const raw = announcement?.links;
							let linksList: string[] = [];
							if (raw != null) {
								if (Array.isArray(raw))
									linksList = raw
										.map((l: unknown) =>
											typeof l === "string"
												? l
												: (l as {url?: string})?.url ?? ""
										)
										.filter(Boolean);
								else if (typeof raw === "string") linksList = [raw];
							}
							return (
								linksList.length > 0 && (
									<>
										<Typography
											className={classes.attachmentsSectionTitle}
											variant="subtitle1"
										>
											Links
										</Typography>
										<Links
											className={classes.attachmentsLinks}
											links={linksList}
										/>
									</>
								)
							);
						})()}
					</DialogContent>

					<DialogActions
						className={classes.dialogActionsRoot}
						aria-label="Announcement author information"
						data-test-id="app2-announcement-modal-footer"
					>
						<AnnouncementAuthorComponent
							author={
								{
									firstname: announcement?.firstname,
									lastname: announcement?.lastname,
									profileImage: announcement?.profileImage ?? undefined,
									jobTitle: announcement?.jobTitle
								} as unknown as IUserEntity
							}
							date={authorDisplayDate}
						/>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
};
